// Shared UI primitives. Every button, chip and switch in the SPA comes from
// here so the chunky "game HUD" look stays consistent.
import { Component, type ButtonHTMLAttributes, type ErrorInfo, type ReactNode } from 'react';
import { Icon, type IconName } from './Icon';

type Tone = 'neutral' | 'sky' | 'sun' | 'leaf' | 'coral' | 'grape' | 'danger';

export function Panel({ title, icon, actions, children, className = '', tone, padded = true }: { title?: ReactNode; icon?: IconName; actions?: ReactNode; children: ReactNode; className?: string; tone?: Tone; padded?: boolean }) {
  return (
    <section className={`panel ${tone ? `tone-${tone}` : ''} ${className}`}>
      {title ? (
        <header className="panel-head">
          <h2>
            {icon ? <Icon name={icon} size={18} /> : null}
            {title}
          </h2>
          {actions ? <div className="panel-actions">{actions}</div> : null}
        </header>
      ) : null}
      <div className={padded ? 'panel-body' : 'panel-body flush'}>{children}</div>
    </section>
  );
}

export function Button({ icon, tone = 'neutral', size = 'md', children, className = '', ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { icon?: IconName; tone?: Tone; size?: 'sm' | 'md' | 'lg' }) {
  return (
    <button type="button" className={`btn btn-${size} tone-${tone} ${className}`} {...rest}>
      {icon ? <Icon name={icon} size={size === 'lg' ? 22 : size === 'sm' ? 15 : 18} /> : null}
      {children ? <span>{children}</span> : null}
    </button>
  );
}

export function IconButton({ icon, label, tone = 'neutral', active, className = '', ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { icon: IconName; label: string; tone?: Tone; active?: boolean }) {
  return (
    <button type="button" className={`icon-btn tone-${tone} ${active ? 'is-active' : ''} ${className}`} aria-label={label} title={label} aria-pressed={active} {...rest}>
      <Icon name={icon} size={18} />
    </button>
  );
}

export function Toggle({ checked, onChange, label, hint, disabled, tone = 'leaf' }: { checked: boolean; onChange: (next: boolean) => void; label: ReactNode; hint?: ReactNode; disabled?: boolean; tone?: Tone }) {
  return (
    <label className={`toggle tone-${tone} ${disabled ? 'is-disabled' : ''}`}>
      <span className="toggle-text">
        <span className="toggle-label">{label}</span>
        {hint ? <span className="toggle-hint">{hint}</span> : null}
      </span>
      <input type="checkbox" role="switch" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span className="toggle-track" aria-hidden>
        <span className="toggle-thumb" />
      </span>
    </label>
  );
}

/** Horizontal bar 0..1 with an optional cap marker. */
export function Meter({ value, max = 1, tone = 'sky', label, detail, compact }: { value: number; max?: number; tone?: Tone; label?: ReactNode; detail?: ReactNode; compact?: boolean }) {
  const pct = max > 0 ? Math.max(0, Math.min(1, value / max)) : 0;
  const over = max > 0 && value >= max;
  return (
    <div className={`meter ${compact ? 'is-compact' : ''}`}>
      {label || detail ? (
        <div className="meter-row">
          <span className="meter-label">{label}</span>
          <span className="meter-detail">{detail}</span>
        </div>
      ) : null}
      <div className={`meter-track tone-${over ? 'danger' : tone}`} role="meter" aria-valuemin={0} aria-valuemax={max} aria-valuenow={value}>
        <div className="meter-fill" style={{ width: `${pct * 100}%` }} />
      </div>
    </div>
  );
}

export function Chip({ tone = 'neutral', icon, children, title }: { tone?: Tone; icon?: IconName; children: ReactNode; title?: string }) {
  return (
    <span className={`chip tone-${tone}`} title={title}>
      {icon ? <Icon name={icon} size={13} /> : null}
      {children}
    </span>
  );
}

export function Segmented<T extends string>({ value, options, onChange, ariaLabel }: { value: T; options: { value: T; label: ReactNode; icon?: IconName }[]; onChange: (v: T) => void; ariaLabel: string }) {
  return (
    <div className="segmented" role="tablist" aria-label={ariaLabel}>
      {options.map((o) => (
        <button key={o.value} type="button" role="tab" aria-selected={o.value === value} className={o.value === value ? 'is-active' : ''} onClick={() => onChange(o.value)}>
          {o.icon ? <Icon name={o.icon} size={15} /> : null}
          <span>{o.label}</span>
        </button>
      ))}
    </div>
  );
}

export function Stat({ label, value, icon, tone = 'neutral', hint }: { label: ReactNode; value: ReactNode; icon?: IconName; tone?: Tone; hint?: ReactNode }) {
  return (
    <div className={`stat tone-${tone}`}>
      {icon ? (
        <span className="stat-icon">
          <Icon name={icon} size={18} />
        </span>
      ) : null}
      <span className="stat-text">
        <span className="stat-value">{value}</span>
        <span className="stat-label">{label}</span>
        {hint ? <span className="stat-hint">{hint}</span> : null}
      </span>
    </div>
  );
}

export function Empty({ icon = 'sparkle', title, children }: { icon?: IconName; title: ReactNode; children?: ReactNode }) {
  return (
    <div className="empty">
      <span className="empty-icon">
        <Icon name={icon} size={28} />
      </span>
      <strong>{title}</strong>
      {children ? <p>{children}</p> : null}
    </div>
  );
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="spinner" role="status" aria-label={label}>
      <span />
      <span />
      <span />
    </div>
  );
}

/** Renders `fallback` while `data` is undefined (a Convex query in flight). */
export function Loading<T>({ data, children, label }: { data: T | undefined; children: (d: T) => ReactNode; label?: string }) {
  if (data === undefined) return <Spinner label={label} />;
  return <>{children(data)}</>;
}

type BoundaryState = { error: Error | null };

/** Catches render/query errors per view so one broken module does not blank the game. */
export class ErrorBoundary extends Component<{ children: ReactNode; label?: string; onUnauthenticated?: () => void }, BoundaryState> {
  override state: BoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): BoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    if (/unauthenticated/i.test(error.message)) this.props.onUnauthenticated?.();
    console.error(`[aa-play] ${this.props.label ?? 'view'} crashed`, error, info.componentStack);
  }

  override render() {
    if (!this.state.error) return this.props.children;
    const msg = this.state.error.message;
    const pending = /not implemented/i.test(msg);
    return (
      <div className="crash">
        <Empty icon={pending ? 'wrench' : 'alert'} title={pending ? 'This part of the world is still under construction' : 'Something tripped over a cable'}>
          {pending ? 'A backend module this view needs has not been built yet.' : msg.slice(0, 240)}
        </Empty>
        <Button icon="refresh" tone="sky" onClick={() => this.setState({ error: null })}>
          Try again
        </Button>
      </div>
    );
  }
}
